import {
  BarChart3,
  BookOpen,
  ClipboardCheck,
  MessageCircle,
  Network,
  ReceiptText,
} from "lucide-react";
import type { ComponentType, SVGProps } from "react";

import { isOwnPayablesRole } from "../invoicing/payableScreenRules";
import { canAccess } from "../providers/commons/canAccess";
import type { AppRole } from "../types";

export interface MobileNavEntry {
  id: string;
  href: string;
  /** i18n key of the label. */
  labelKey: string;
  /** Passed to `translate` for plural resource names. */
  smartCount?: number;
  /** Resource whose `list` access gates the entry. */
  resource: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
}

/** Almikia section: every role reaches it, so it never counts as an "extra". */
export const ALMIKIA_ENTRY: MobileNavEntry = {
  id: "almikia",
  href: "/almikia",
  labelKey: "crm.almikia.nav_label",
  resource: "org_chart_people",
  Icon: Network,
};

/**
 * Destinations that do not fit the 6-slot floating pill. Adding a destination
 * later means adding one entry here.
 */
export const MOBILE_EXTRA_ENTRIES: MobileNavEntry[] = [
  {
    id: "sales_tools",
    href: "/sales_tools",
    labelKey: "crm.salesTools.nav",
    resource: "sales_tools",
    Icon: BookOpen,
  },
  {
    id: "expenses",
    href: "/expenses",
    labelKey: "resources.expenses.name",
    smartCount: 2,
    resource: "expenses",
    Icon: ReceiptText,
  },
  {
    id: "meta_campaign_report",
    href: "/meta_campaign_report",
    labelKey: "crm.metaOrigin.report.nav",
    resource: "meta_campaign_report",
    Icon: BarChart3,
  },
  // Almikia Epic EW2 (TASK-080): direccion and lead_manager only (an asesora has no Inbox).
  {
    id: "whatsapp_inbox",
    href: "/whatsapp_inbox",
    labelKey: "crm.whatsapp.inbox.nav",
    resource: "whatsapp_inbox",
    Icon: MessageCircle,
  },
  // Almikia batch EQ3 (TASK-115): the approver's list; direccion only here (an approver of
  // another role reaches it from the notice in a lead's quoter panel).
  {
    id: "plans_to_approve",
    href: "/planes-por-aprobar",
    labelKey: "crm.quoter.approval.list_title",
    resource: "quoter_plan_approvers",
    Icon: ClipboardCheck,
  },
];

/**
 * Entries of the mobile "Mas" overflow for a role. When at least one extra
 * destination is allowed, "Mas" appears and also holds Almikia (so the pill
 * stays at 6 slots); otherwise `entries` is empty and no "Mas" is shown.
 */
export const getMobileOverflowEntries = (role: AppRole): MobileNavEntry[] => {
  const allowed = (entry: MobileNavEntry) =>
    canAccess(role, { resource: entry.resource, action: "list" });
  const extras = MOBILE_EXTRA_ENTRIES.filter(allowed);
  if (extras.length === 0) return [];
  return [ALMIKIA_ENTRY, ...extras].filter(allowed);
};

/** i18n key of the desktop rail payables entry: "Mis pagos" for own-rows roles. */
export const getPayablesNavLabelKey = (role: AppRole): string =>
  isOwnPayablesRole(role)
    ? "crm.invoicing.payables.my_title"
    : "resources.payables.name";
